import type { MouseEvent, SyntheticEvent } from 'react'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'

// Legacy paging look — self-contained (markup + wiring + Tailwind).
// Pick a look by importing PagingBar from this folder or from ../Modern.
// SKELETON: the hook classes md-paging / md-paging__page / md-paging__sequence /
// md-hook-disabled and the contract attrs are load-bearing — the legacy prev/next
// click router (MsgDeskApp) resolves against them. Keep them on both variants.

function stopEvent(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

function hookAttrs(className: string, legacyClass: string) {
  return {
    className,
    'data-md-legacy-class': legacyClass,
  }
}

export function PagingBar({
  section,
  currentPage = 1,
  totalPages = 1,
  registerContract = true,
}: {
  section: 0 | 1
  currentPage?: number
  totalPages?: number
  registerContract?: boolean
}) {
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const paging = createPagingModel(currentPage, totalPages)
  const rootClass = 'md-hook-no-select md-paging float-left flex select-none items-center gap-[5px]'
  const pageClass = 'md-paging__page flex cursor-pointer items-center bg-white px-0.5 py-px text-[90%] text-black'
  const currentClass = `${pageClass} md-paging__page--current md-hook-current-page cursor-default bg-green-700 px-[5px] text-white`
  const disabledClass = `${pageClass} md-hook-disabled cursor-default text-[#a9a9a9]`

  // BEHAVIOUR: jump to a page / step prev-next through the runtime bridge.
  function pageClick(event: MouseEvent<HTMLElement>, page: PagingTarget) {
    stopEvent(event)
    if (page === 'previous' && paging.previousDisabled) return
    if (page === 'next' && paging.nextDisabled) return
    dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget })
  }

  return (
    <div
      {...(registerContract
        ? legacyHookAttrs(contract, rootClass)
        : {
            className: rootClass,
            'data-md-section': section === 0 ? 'answers' : 'submsges',
          })}
    >
      {paging.tokens.map((token, index) => token === 'gap' ? (
        <span key={`gap-${index}`} className="md-paging__gap flex items-center">.,.</span>
      ) : (
        <a
          key={token}
          href="#"
          {...hookAttrs(
            token === paging.currentPage
              ? currentClass
              : pageClass,
            token === paging.currentPage ? 'mcss-current_page-cssm' : 'mcss-page_el-cssm',
          )}
          onClick={(e) => pageClick(e, token)}
        >
          {token}
        </a>
      ))}
      {/* Disabled edge buttons stay MOUNTED but display:none — the legacy
          prev/next click router resolves prev vs next by index inside this
          sequence span, so unmounting Prev would misroute Next as "prev". */}
      <span className="md-paging__sequence flex items-center gap-[5px]">
        <a
          href="#"
          {...hookAttrs(
            paging.previousDisabled ? disabledClass : pageClass,
            paging.previousDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm',
          )}
          style={paging.previousDisabled ? { display: 'none' } : undefined}
          onClick={(e) => pageClick(e, 'previous')}
        >
          Previous
        </a>
        <a
          href="#"
          {...hookAttrs(
            paging.nextDisabled ? disabledClass : pageClass,
            paging.nextDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm',
          )}
          style={paging.nextDisabled ? { display: 'none' } : undefined}
          onClick={(e) => pageClick(e, 'next')}
        >
          Next
        </a>
      </span>
    </div>
  )
}
